import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiCalendar,
  FiClock,
  FiMapPin,
  FiUsers,
  FiUserPlus,
  FiCheck,
  FiLoader,
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AvatarBadge from './AvatarBadge';
import WeatherBadge from './WeatherBadge';
import { joinOpenRound } from '../services/roundService';
import { formatRelativeDate } from '../utils/formatRelativeDate';
import { formatRoundTimeWithViewerNote } from '../utils/timeHelpers';
import { useToast } from '../contexts/ToastContext';

const formatTime12h = (timeStr) => {
  if (!timeStr) return '';
  const [h, m] = String(timeStr).split(':');
  const hour = parseInt(h, 10);
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${m} ${period}`;
};

function OpenRoundCard({ round, onJoined }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState(false);

  const full = (round.current_player_count ?? 0) >= (round.max_players ?? 4);
  const datePhrase = formatRelativeDate(round.round_date);
  const slotsRemaining = Math.max(0, round.slots_remaining ?? 0);
  const isInstant = round.join_mode === 'instant';

  const players = Array.isArray(round.players) ? round.players.filter((p) => p?.name) : [];

  const handleJoin = async (e) => {
    e.stopPropagation();
    if (busy || joined || full) return;
    setBusy(true);
    const { data, error } = await joinOpenRound(round.id);
    setBusy(false);
    if (error || !data) {
      toast.error('Something went wrong. Try again.');
      return;
    }
    switch (data.outcome) {
      case 'joined':
        setJoined(true);
        toast.success(`You're in for ${round.course || 'the round'}.`);
        onJoined?.(round);
        break;
      case 'requested':
        setJoined(true);
        toast.success('Request sent. The host will review it.');
        onJoined?.(round);
        break;
      case 'already_in':
        toast.info('You\'re already in this round.');
        onJoined?.(round);
        break;
      case 'full':
        toast.warning('This round just filled up.');
        onJoined?.(round);
        break;
      default:
        toast.warning('You can\'t join this round.');
    }
  };

  const handleOpen = () => navigate(`/round/${round.short_code}`);

  const buttonLabel = (() => {
    if (joined) return isInstant ? 'Joined' : 'Requested';
    if (full) return 'Full';
    return isInstant ? 'Join Round' : 'Request to join';
  })();

  const buttonIcon = joined ? FiCheck : FiUserPlus;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={handleOpen}
      className="bg-accent/[0.04] rounded-xl border border-accent/30 p-3.5 cursor-pointer hover:border-accent/60 transition-colors"
    >
      <div className="flex items-start justify-between gap-3 mb-2.5">
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-text-primary truncate">
            {round.course || 'Round'}
          </h4>
          <p className="text-xs text-text-secondary mt-0.5 truncate">
            Hosted by {round.creator_name || 'a player'}
            {round.group_name ? ` · ${round.group_name}` : ''}
          </p>
        </div>
        <span
          className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full whitespace-nowrap ${
            isInstant ? 'bg-accent/10 text-accent' : 'bg-surface text-text-secondary'
          }`}
        >
          {isInstant ? 'Open · Join' : 'Open · Request'}
        </span>
      </div>

      {/* Who's playing */}
      <div className="flex items-center gap-2 mb-2.5 min-w-0">
        {players.length > 0 ? (
          <>
            <div className="flex -space-x-2 flex-shrink-0">
              {players.slice(0, 4).map((p, i) => (
                <AvatarBadge
                  key={`${p.name}-${i}`}
                  name={p.name}
                  size="sm"
                  owner={i === 0}
                  className="ring-2 ring-card"
                />
              ))}
            </div>
            <span className="text-xs text-text-secondary truncate min-w-0">
              {players.map((p) => p.name).join(', ')}
              {full ? '' : slotsRemaining > 0
                ? ` · ${slotsRemaining} spot${slotsRemaining === 1 ? '' : 's'} left`
                : ''}
            </span>
          </>
        ) : (
          <span className="flex items-center gap-1.5 text-xs text-text-secondary">
            <SafeIcon icon={FiUsers} className="w-3.5 h-3.5" />
            {round.current_player_count} of {round.max_players}
            {slotsRemaining > 0 && !full && (
              <span className="text-text-muted">
                · {slotsRemaining} spot{slotsRemaining === 1 ? '' : 's'} left
              </span>
            )}
          </span>
        )}
      </div>

      {/* Meta: date, time, weather */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-text-secondary mb-3">
        <span className="flex items-center gap-1.5">
          <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5" />
          {datePhrase}
        </span>
        {(() => {
          const { primary, secondary } = formatRoundTimeWithViewerNote(round, { hour12: true });
          return (
            <span className="flex flex-col gap-0.5">
              <span className="flex items-center gap-1.5">
                <SafeIcon icon={FiClock} className="w-3.5 h-3.5" />
                {primary || formatTime12h(round.round_time)}
              </span>
              {secondary && (
                <span className="text-[11px] text-text-tertiary ml-5">{secondary}</span>
              )}
            </span>
          );
        })()}
        {round.course && (
          <span className="flex items-center gap-1.5 max-w-full min-w-0">
            <SafeIcon icon={FiMapPin} className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{round.course}</span>
          </span>
        )}
        {round.weather_golf_score && (
          <WeatherBadge
            golfScore={round.weather_golf_score}
            golfCategory={round.weather_golf_category}
            compact
          />
        )}
      </div>

      <button
        type="button"
        onClick={handleJoin}
        disabled={busy || joined || full}
        className={`w-full px-4 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
          joined
            ? 'bg-status-success/10 text-status-success'
            : full
            ? 'bg-surface text-text-muted cursor-not-allowed'
            : 'bg-accent text-white hover:bg-interactive-hover'
        }`}
      >
        {busy ? (
          <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
        ) : (
          <SafeIcon icon={buttonIcon} className="w-4 h-4" />
        )}
        {buttonLabel}
      </button>
    </motion.div>
  );
}

export default OpenRoundCard;
